import React, { useState, useEffect, Fragment } from 'react';
import { Modal, Button, Card, Form, Spinner } from 'react-bootstrap';
import { FaStickyNote, FaPlus, FaThumbtack, FaRegClock, FaUser, FaRegStickyNote, FaArrowLeft } from 'react-icons/fa';
import { BiArchiveIn, BiArchiveOut } from 'react-icons/bi';
import { css } from 'twin.macro';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { format } from 'date-fns';
import ActiveArchiveToggle from '@/components/Filters/ActiveArchiveToggle';
import { clientNotesApi } from '@/_services/api/clientNotes';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { ActionStyles } from '@/components/Admin/reusableStyles';
import { BsEye, BsPencil } from 'react-icons/bs';
import { FiSearch } from 'react-icons/fi';
import { SearchBox } from '@/components/Admin/reusableComponents/AdminSearchBox';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import OurAccordion from '@/_elements/OurAccordion';

const Component = ({ client }) => {
    const [showModal, setShowModal] = useState(false);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [mode, setMode] = useState('list');
    const [currentNote, setCurrentNote] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (showModal && mode === 'list') {
            fetchNotes();
        }
        if (!showModal) {
            setSearchTerm('');
            setIsActive(true);
            setNotes([]);
            setLoading(true);
        }
    }, [showModal, isActive]);

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const response = await clientNotesApi.getClientNotes({ client: client?._id, archived: !isActive });
            setNotes(response?.results || []);
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Error fetching notes:', error);
            toast.error('Failed to fetch notes');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNote = () => {
        setCurrentNote({
            title: '',
            content: '',
            client: client?._id,
        });
        setMode('create');
    };

    const handleEditNote = (note) => {
        setCurrentNote(note);
        setMode('edit');
    };
    const handleViewNote = (note) => {
        setCurrentNote(note);
        setMode('view');
    };

    const handleSaveNote = async (noteData) => {
        try {
            setSaving(true);
            const allowedKeys = ['title', 'content', 'client'];
            const filteredData = {};
            allowedKeys.forEach((key) => {
                if (noteData[key] !== undefined) {
                    filteredData[key] = noteData[key];
                }
            });

            if (noteData._id) {
                await clientNotesApi.updateClientNote(noteData._id, filteredData);
                toast.success('Note updated successfully');
            } else {
                await clientNotesApi.createClientNote(filteredData);
                toast.success('Note created successfully');
            }

            return true;
        } catch (error) {
            console.error('Error saving note:', error);
            toast.error('Failed to save note');
            return false;
        } finally {
            setSaving(false);
            await fetchNotes();
        }
    };

    const handleArchive = async (note) => {
        const result = await Swal.fire({
            title: `Archive Note`,
            html: `Are you sure you want to archive the note: <strong>"${note.title}"</strong>?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, archive it',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            await clientNotesApi.patchClientNote(note._id, {
                archived: true,
            });
            setNotes((prevNotes) => prevNotes.filter((n) => n._id !== note._id));
            toast.success('Note archived successfully');
        } catch (error) {
            console.error('Error archiving note:', error);
            toast.error('Failed to archive note');
        }
    };

    const handleUnarchive = async (note) => {
        const result = await Swal.fire({
            title: `Unarchive Note`,
            html: `Are you sure you want to unarchive the note: <strong>"${note.title}"</strong>?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, unarchive it',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            await clientNotesApi.patchClientNote(note._id, {
                archived: false,
            });
            setNotes((prevNotes) => prevNotes.filter((n) => n._id !== note._id));
            toast.success('Note unarchived successfully');
        } catch (error) {
            console.error('Error unarchiving note:', error);
            toast.error('Failed to unarchive note');
        }
    };

    const [filteredNotes, setFilteredNotes] = useState(notes);
    useEffect(() => {
        setFilteredNotes(
            notes.filter(
                (note) =>
                    note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    note.content?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [notes, searchTerm]);

    const handleCloseModal = () => {
        setShowModal(false);
        setMode('list');
        setCurrentNote(null);
        setSaving(false);
    };

    return (
        <>
            <BootstrapToolTip
                text={'Client Notes'}
                cssStyles={'top:-70% !important; min-width: fit-content !important;'}
            >
                <FaStickyNote
                    className="icon edit"
                    onClick={() => setShowModal(true)}
                    style={{ cursor: 'pointer', fontSize: '1.5rem' }}
                />
            </BootstrapToolTip>

            <Modal show={showModal} onHide={handleCloseModal} centered size="xl" backdrop="static">
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="d-flex align-items-center">
                        <FaStickyNote color="#ea9941" className="mr-1" />
                        <strong className="ml-2">
                            {mode === 'list' && 'Client Notes'}
                            {mode === 'create' && 'New Clinical Note'}
                            {mode === 'edit' && 'Edit Note'}
                            {mode === 'view' && 'View Note'}
                        </strong>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="modal-body p-0" style={{ minHeight: '80vh' }}>
                    <div className="d-flex flex-column h-100">
                        {mode === 'list' ? (
                            <>
                                <div className="p-3 border-bottom bg-light">
                                    <div className="row g-2 align-items-center">
                                        <div className="col-md-4">
                                            <SearchBox>
                                                <input
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    value={searchTerm}
                                                    placeholder={'Search notes by title/content...'}
                                                    className="search-input search-area-items"
                                                    disabled={loading}
                                                />
                                                <p className="search-area-items">
                                                    <FiSearch
                                                        size="15"
                                                        className="mr-1 d-flex justify-content-center align-items-center"
                                                    />
                                                </p>
                                            </SearchBox>
                                        </div>

                                        <div className="col-md-8 d-flex justify-content-end gap-2">
                                            <div className="mr-4">
                                                <ActiveArchiveToggle
                                                    isActive={isActive}
                                                    setIsActive={setIsActive}
                                                    disabled={loading}
                                                />
                                            </div>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={handleCreateNote}
                                                disabled={loading}
                                            >
                                                <FaPlus className="mr-1" />
                                                New Note
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3 flex-grow-1">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2">Loading notes...</p>
                                        </div>
                                    ) : filteredNotes.length === 0 ? (
                                        <div className="text-center py-5">
                                            <FaRegStickyNote size={48} className="text-muted mb-3" />
                                            <p className="text-muted">No notes found</p>
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            {filteredNotes
                                                .sort((a, b) => {
                                                    const pinnedDiff = b.pinned - a.pinned;
                                                    if (pinnedDiff !== 0) {
                                                        return pinnedDiff;
                                                    }

                                                    const dateA = new Date(a.createdAt);
                                                    const dateB = new Date(b.createdAt);
                                                    return dateB - dateA;
                                                })
                                                .map((note) => (
                                                    <div className="col-12" key={note._id}>
                                                        <NoteCard
                                                            note={note}
                                                            setNotes={setNotes}
                                                            onEdit={() => handleEditNote(note)}
                                                            onView={() => handleViewNote(note)}
                                                            onArchive={
                                                                isActive
                                                                    ? () => handleArchive(note)
                                                                    : () => handleUnarchive(note)
                                                            }
                                                            isActiveView={isActive}
                                                            mode={mode}
                                                        />
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <NoteEditor
                                mode={mode}
                                note={currentNote}
                                onSave={handleSaveNote}
                                onCancel={() => {
                                    setMode('list');
                                    setCurrentNote(null);
                                }}
                                saving={saving}
                            />
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

const NoteCard = ({ mode, note, onView, onEdit, onArchive, isActiveView, setNotes }) => {
    const [isBusy, setIsBusy] = useState(false);
    const handleTogglePin = async () => {
        try {
            if (isBusy) {
                return;
            }
            setIsBusy(true);
            await clientNotesApi.patchClientNote(note._id, {
                pinned: !note.pinned,
            });
            setNotes((prevNotes) =>
                prevNotes.map((n) =>
                    n._id === note._id
                        ? {
                              ...n,
                              pinned: !n.pinned,
                          }
                        : n
                )
            );
            toast.success(`Note ${note.pinned ? 'unpinned' : 'pinned'} successfully`);
        } catch (error) {
            console.error('Error toggling pin:', error);
            toast.error('Failed to toggle pin');
        } finally {
            setIsBusy(false);
        }
    };

    const handleArchive = async () => {
        if (isBusy) {
            return;
        }
        setIsBusy(true);
        await onArchive(note);
        setIsBusy(false);
    };

    return (
        <Card
            className={`${note.pinned ? 'border-primary' : ''} mt-4`}
            css={css`
                // border-width: ${note.pinned ? '3px' : '1px'};
                border-radius: 4px;
                box-shadow:
                    rgba(50, 50, 93, 0.25) 0px 2px 5px -1px,
                    rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
            `}
        >
            <Card.Body
                css={css`
                    padding: 0px 0px 1.2rem 0px;
                `}
            >
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1">
                        <OurAccordion
                            title={
                                <div className="d-flex align-items-center" style={{ padding: '0 4px' }}>
                                    {note?.pinned && (
                                        <div
                                            css={css`
                                                svg {
                                                    transform: rotate(-45deg);
                                                }
                                            `}
                                        >
                                            <FaThumbtack className="red mr-2 mb-1" />
                                        </div>
                                    )}
                                    <strong>
                                        <pre
                                            className="mb-0"
                                            css={css`
                                                font-family: inherit;
                                                line-height: 1.4;
                                                margin: 0;
                                                display: -webkit-box;
                                                -webkit-line-clamp: 3;
                                                -webkit-box-orient: vertical;
                                                overflow: hidden;
                                                text-overflow: ellipsis;
                                                white-space: pre-wrap;
                                                word-wrap: break-word;
                                                color: #50aeb0;
                                            `}
                                        >
                                            {note.title}
                                        </pre>
                                    </strong>
                                </div>
                            }
                            defaultShow={false}
                            titleStyle={{
                                color: '#50aeb0',
                                padding: '0.5rem 0',
                            }}
                            style={{
                                marginBottom: '1rem',
                            }}
                        >
                            <div className="mb-3">
                                <pre
                                    className="mb-0"
                                    css={css`
                                        font-family: inherit;
                                        line-height: 1.4;
                                        margin: 0;
                                        white-space: pre-wrap;
                                        word-wrap: break-word;
                                    `}
                                >
                                    {note.content}
                                </pre>
                            </div>
                        </OurAccordion>
                    </div>
                </div>
                <div className="d-flex justify-content-between align-items-center mr-2 ml-2">
                    <NoteCreatorDetails note={note} mode={mode} />

                    <div className="d-flex gap-1 ml-3">
                        <ActionStyles>
                            <BootstrapToolTip text={'View'}>
                                <BsEye className="icon view" onClick={onView} disabled={isBusy} />
                            </BootstrapToolTip>

                            {isActiveView && (
                                <BootstrapToolTip text={'Edit'}>
                                    <BsPencil className="icon edit" onClick={onEdit} disabled={isBusy} />
                                </BootstrapToolTip>
                            )}
                            {isActiveView ? (
                                <BootstrapToolTip text={'Archive'}>
                                    <BiArchiveIn className="icon archiveIn" onClick={handleArchive} disabled={isBusy} />
                                </BootstrapToolTip>
                            ) : (
                                <BootstrapToolTip text={'Unarchive'}>
                                    <BiArchiveOut
                                        className="icon archiveOut"
                                        onClick={handleArchive}
                                        disabled={isBusy}
                                    />
                                </BootstrapToolTip>
                            )}
                            {isActiveView && (
                                <BootstrapToolTip text={note.pinned ? 'Unpin' : 'Pin'}>
                                    <FaThumbtack
                                        className={`icon ${note.pinned ? 'delete' : 'gray'}`}
                                        onClick={handleTogglePin}
                                        disabled={isBusy}
                                    />
                                </BootstrapToolTip>
                            )}
                        </ActionStyles>
                    </div>
                </div>
                {/* Creator details outside accordion */}
            </Card.Body>
        </Card>
    );
};

const NoteCreatorDetails = ({ note, mode }) => {
    if (!note || !note?.createdBy) {
        return null;
    }
    return (
        <div className="d-flex flex-column">
            <div className="d-flex justify-content-between align-items-center text-muted small  ">
                <div className="d-flex align-items-center">
                    <FaUser className="mr-2" size={12} />
                    <span>Created By: {note?.createdBy?.name}</span>
                    <span className="ml-2">|</span>
                    <FaRegClock className="mr-1 ml-2" size={12} />
                    <span>Created At: {format(new Date(note.createdAt), 'MMM dd, HH:mm')}</span>
                </div>
            </div>
            {note?.updatedBy && mode !== 'list' && (
                <div className="d-flex justify-content-between align-items-center text-muted small border-top pt-2">
                    <div className="d-flex align-items-center">
                        <FaUser className="mr-2" size={12} />
                        <span> Updated By: {note?.updatedBy?.name}</span>
                        <span className="ml-2">|</span>
                        <FaRegClock className="mr-1 ml-2" size={12} />
                        <span> Updated At: {format(new Date(note.updatedAt), 'MMM dd, HH:mm')}</span>
                    </div>
                </div>
            )}
        </div>
    );
};
const NoteEditor = ({ mode, note, onSave = () => {}, onCancel = () => {}, saving }) => {
    const [title, setTitle] = useState(note?.title || '');
    const [content, setContent] = useState(note?.content || '');

    useEffect(() => {
        if (note) {
            setTitle(note.title || '');
            setContent(note.content || '');
        }
    }, [note]);

    const handleSave = async () => {
        try {
            if (!title.trim() || !content.trim()) {
                toast.error('Please fill in both title and content');
                return;
            }

            const noteData = {
                ...note,
                title,
                content,
            };

            await onSave(noteData);
            onCancel();
        } catch (error) {
            console.error('Error saving note:', error);
            toast.error('Error saving note. Please try again.');
        }
    };

    const isNewNote = !note?._id;

    return (
        <div className="p-3 d-flex flex-column h-100">
            <div>
                <Button variant="link" onClick={onCancel} disabled={saving} style={{ paddingLeft: 0 }}>
                    <FaArrowLeft size={20} /> <span className="ml-2">Back</span>
                </Button>
            </div>
            <Form className="flex-grow-1 d-flex flex-column">
                <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Enter title"
                        maxLength={150}
                        disabled={saving || mode === 'view'}
                    />
                </Form.Group>

                <Form.Group className="flex-grow-1 d-flex flex-column mb-3">
                    <Form.Label>Content</Form.Label>
                    <Form.Control
                        as="textarea"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Enter clinical notes..."
                        disabled={saving || mode === 'view'}
                        style={{
                            minHeight: '300px',
                        }}
                    />
                </Form.Group>
                <div className="mt-3">
                    <NoteCreatorDetails note={note} mode={mode} />
                </div>
                <div className="border-top pt-3 mt-auto">
                    <div className="d-flex justify-content-end gap-2">
                        {mode !== 'view' && (
                            <Button
                                variant="primary"
                                className="ml-2"
                                onClick={handleSave}
                                disabled={saving || !title.trim() || !content.trim()}
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="mr-2" />
                                        {isNewNote ? 'Creating...' : 'Saving...'}
                                    </>
                                ) : isNewNote ? (
                                    'Create Note'
                                ) : (
                                    'Save Changes'
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            </Form>
        </div>
    );
};

const ClinicalNotes = (props) => (
    <ComponentErrorBoundary props={props}>
        <Component {...props} />
    </ComponentErrorBoundary>
);
export default ClinicalNotes;
